import React, { useState } from 'react';
import gql from 'graphql-tag';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { MutationFn } from 'react-apollo';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { CoreAdminReportsGenerateAdminReport } from '../../schemas/rql';
import { RCAlertBox, RCButton, RCCombobox, RCTextInput } from '@reverbdotcom/cadence/components';

type GenerateAdminReportMutationFn = MutationFn<
  CoreAdminReportsGenerateAdminReport.Mutation,
  CoreAdminReportsGenerateAdminReport.Variables
>;

interface MutationProps {
  generateAdminReport: GenerateAdminReportMutationFn;
}


interface FormProps {
  reports: string[];
}

type Props = FormProps & MutationProps;

function AccountingReportForm({
  reports,
  generateAdminReport,
}: Props) {
  const [selectedReport, selectReport] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [successAlert, setSuccessAlert] = useState(false);

  const [mutate, { errors }] = useMutation(generateAdminReport);

  async function onClick(e) {
    e.preventDefault();

    const result = await mutate({
      variables: {
        input: {
          reportName: selectedReport,
          startDate: startDate,
          endDate: endDate,
        },
      },
    });

    if (result) {
      setSuccessAlert(result?.data?.generateAdminReport?.reportEnqueued);
    }
  }

  const reportOptions = reports.map(name => ({ label: name, value: name }));

  const invalidDates = startDate && endDate && startDate >= endDate;

  function invalidDatesErrorText() {
    if (invalidDates) {
      return 'Please check that the selected start date is before the end date';
    }
    return null;
  }

  function isSubmitDisabled() {
    if (!selectedReport || !startDate || !endDate || invalidDates) {
      return true;
    }
  }

  return (
    <div className="g-container">
      <div className="g-col-8 g-offset-2">
        <header className="cms-h1"><b>Accounting Report Request Form</b></header>
        <p>
          This form allows you to generate ad hoc accounting reports with custom dates.
          Your requested report will be emailed to <u>accounting-reports@reverb.com</u> when the report query is completed.
        </p>
        <b>Things to note:</b>
        <ul className="ml-4" style={{ listStyleType: 'disc' }}>
          <li>
            Your report may take a while (1-3+ hours) to generate, particularly if the dates provided are for a long interval.
            Please be patient in receiving your report and refrain from requesting the same report multiple times.
            This may result in a logjam of requested reports.
          </li>
          <li>
            Per-currency reports (e.g., statements_by_item_type) will be auto-generated,
            so you will receive reports for all four currencies (CAD/EUR/GBP/USD).
          </li>
          <li>
            Per-region/country reports (e.g., taxable_seller_fees_report) will be auto-generated,
            so you will receive reports for all of the regions we currently generate at month-end.
          </li>
          <li>
            Though all other reports use UTC, adyen_marketplace_payments_accounting_report has a custom offset and uses the CET time zone.
          </li>
        </ul>
        <div className="mt-4">
          {successAlert && (
            <RCAlertBox type="success">
              Your request is being processed. When the report is complete, it will be emailed to accounting-reports@reverb.com.
            </RCAlertBox>
          )}
          { errors.length > 0 && (
            <RCAlertBox type="error">
              {errors.map((error) => {
                return (
                  <p>
                    { error.message }
                  </p>
                );
              })}
            </RCAlertBox>
          )}
        </div>
        <div className="width-rem-60 mtb-4">
          <form>
            <RCCombobox
              label="Report Name"
              options={reportOptions}
              onOptionSelect={e => selectReport(e.value)}
              required
              value={selectedReport}
            />
            <RCTextInput
              errorText={invalidDatesErrorText()}
              helpText="Report dates are in UTC"
              label="Start Date"
              onChange={e => setStartDate(e.target.value)}
              required
              type="date"
              value={startDate}
            />
            <RCTextInput
              errorText={invalidDatesErrorText()}
              helpText="The end date is exclusive, e.g., an end date of 01/01/2023 will include records through 12/31/2022"
              label="End Date"
              onChange={e => setEndDate(e.target.value)}
              required
              type="date"
              value={endDate}
            />
            <RCButton
              disabled={isSubmitDisabled()}
              isSubmit
              onClick={onClick}
              variant="primary"
            >
              Submit
            </RCButton>
          </form>
        </div>
      </div>
    </div>
  );
}

const withMutation = withGraphql<
  FormProps,
  GenerateAdminReportMutationFn
>(
  gql`
    mutation Core_AdminReports_GenerateAdminReport(
      $input: Input_core_apimessages_GenerateAdminReportRequest
    ) {
      generateAdminReport(input: $input) {
        reportEnqueued
      }
    }
  `,
  {
    name: 'generateAdminReport',
  },
);

export default withMutation(AccountingReportForm);
